import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiArrowLeft } from 'react-icons/fi';
import timeTrackingService from '../../services/timeTrackingService';
import projectService from '../../services/projectService';
import taskService from '../../services/taskService';
import TimeEntryForm from '../../components/TimeTracking/TimeEntryForm';

const TimeEntryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch time entry
  const { data: entryData, isLoading: entryLoading } = useQuery({
    queryKey: ['timeEntry', id],
    queryFn: () => timeTrackingService.getTimeEntry(id),
  });

  // Fetch projects
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await projectService.getAllProjects({ limit: 1000 });
      return response;
    },
  });

  // Fetch tasks
  const { data: tasksData } = useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const response = await taskService.getAllTasks({ limit: 1000 });
      return response;
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => timeTrackingService.updateTimeEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['timeEntries']);
      queryClient.invalidateQueries(['timeEntry', id]);
      toast.success('Time entry updated successfully');
      navigate('/app/time-tracking');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update time entry');
    },
  });

  const handleSubmit = async (data) => {
    await updateMutation.mutateAsync(data);
  };

  const handleCancel = () => {
    navigate('/app/time-tracking');
  };

  if (entryLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!entryData?.data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Time entry not found</p>
        <button
          onClick={() => navigate('/app/time-tracking')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Time Tracking
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/app/time-tracking')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back to Time Tracking
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Edit Time Entry</h1>
        <p className="mt-1 text-sm text-gray-600">
          Update your time entry details
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <TimeEntryForm
          entry={entryData.data}
          projects={projectsData?.projects || []}
          tasks={tasksData?.data?.tasks || []}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default TimeEntryEdit;

